import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { CalsoftNavigationItem } from '../../../navigation.types';
import { CalsoftVerticalNavigationComponent } from '../../vertical.component';
import { CalsoftNavigationService } from '../../../navigation.service';
import { NavigationEnd, Router } from '@angular/router';

@Component({
  selector: 'calsoft-vertical-navigation-aside-item',
  templateUrl: './aside.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CalsoftVerticalNavigationAsideItemComponent implements OnInit, OnDestroy {

  url = '';


  active: boolean;
  // Hover state (used to show subtitle/submenu on hover)
  hovered: boolean = false;
  // Whether the aside was opened due to hover (so we can close it on leave)
  private _hoverOpenedAside: boolean = false;

  // Auto collapse
  @Input()
  autoCollapse: boolean;

  // Item
  @Input()
  item: CalsoftNavigationItem;

  // Name
  @Input()
  name: string;

  // Skip children
  @Input()
  skipChildren: boolean;

  // Private
  private _activeItemId: string;
  private _calsoftVerticalNavigationComponent: CalsoftVerticalNavigationComponent;
  private _unsubscribeAll: Subject<any>;

  /**
   * Constructor
   *
   * @param {CalsoftNavigationService} _calsoftNavigationService
   * @param {ChangeDetectorRef} _changeDetectorRef
   * @param {Router} _router
   */
  constructor(
    private _calsoftNavigationService: CalsoftNavigationService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _router: Router,
  ) {
    // Set the private defaults
    this._unsubscribeAll = new Subject();

    // Set the defaults
    this.skipChildren = false;
      this.url = this._router.url || '';
          this._router.events
            .pipe(filter((e: any) => e instanceof NavigationEnd), takeUntil(this._onDestroy))
            .subscribe(() => { this.url = this._router.url || ''; });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Setter & getter for activeItemId
   *
   * @param value
   */
  @Input()
  set activeItemId(value: any) {
    // If the value is the same, return...
    if (this._activeItemId === value) {
      return;
    }

    // Store the value
    this._activeItemId = value;

    // Mark if active
    this._markIfActive(this._router.url);
  }

  get activeItemId(): string {
    return this._activeItemId;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Get the parent navigation component
    this._calsoftVerticalNavigationComponent = this._calsoftNavigationService.getComponent(this.name);

    // Mark if active
    this._markIfActive(this._router.url);

    // Attach a listener to the NavigationEnd event
    this._router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe((event: NavigationEnd) => {

        // Mark if active
        this._markIfActive(event.urlAfterRedirects);
      });


    // Subscribe to onRefreshed on the navigation component
    this._calsoftVerticalNavigationComponent.onRefreshed.pipe(
      takeUntil(this._unsubscribeAll)
    ).subscribe(() => {

      // Mark for check
      this._changeDetectorRef.markForCheck();
    });
  }

  // Called from template on mouse enter
  onMouseEnter(): void {
    this.hovered = true;

    // Open aside on hover if navigation component available and item is not disabled
    if ( this._calsoftVerticalNavigationComponent && !this.item.disabled )
    {
      // Only open if it's not already the active aside
      if ( this._calsoftVerticalNavigationComponent.activeAsideItemId !== this.item.id )
      {
        this._calsoftVerticalNavigationComponent.openAside(this.item);
        this._hoverOpenedAside = true;
      }
    }
  }

  // Called from template on mouse leave
  onMouseLeave(): void {
    this.hovered = false;

    // If we opened the aside due to hover, close it now — but wait a short moment
    // to allow the mouseenter to fire on the aside wrapper (when moving from
    // the menu item to the submenu). Only close if the aside is not hovered.
    if ( this._hoverOpenedAside && this._calsoftVerticalNavigationComponent )
    {
      setTimeout(() => {
        // If aside wrapper isn't hovered, close it
        if ( !this._calsoftVerticalNavigationComponent.asideHovered )
        {
          this._calsoftVerticalNavigationComponent.closeAside();
          this._hoverOpenedAside = false;
        }
      }, 120);
    }
  }

  @HostListener('click')
  onHostClick(): void {
    // If the user clicks the item, treat it as manual open and stop auto-closing on mouseleave
    this._hoverOpenedAside = false;
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
      this._onDestroy.next();
      this._onDestroy.complete();
  }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Check if the given item has the given url
     * in one of its children
     *
     * @param item
     * @param url
     * @private
     */
    private _hasCurrentUrlAsChildren(item, url): boolean
    {
        const children = item.children;

        if ( !children )
        {
            return false;
        }

        for ( const child of children )
        {
            if ( child.children )
            {
                if ( this._hasCurrentUrlAsChildren(child, url) )
                {
                    return true;
                }
            }

            // Skip items other than 'basic'
            if ( child.type !== 'basic' )
            {
                return false;
            }

            // Check if the item's link is the exact same of the
            // current url
            if ( child.link === url )
            {
                return true;
            }

            // If exactMatch is not set for the item, also check
            // if the current url starts with the item's link and
            // continues with a question mark, a pound sign or a
            // slash
            if ( !child.exactMatch && (child.link === url || url.startsWith(child.link + '?') || url.startsWith(child.link + '#') || url.startsWith(child.link + '/')) )
            {
                return true;
            }
        }

        return false;
    }

    /**
     * Decide and mark if the item is active
     *
     * @private
     */
    private _markIfActive(url): void
    {
        // Check if the activeItemId is equals to this item id
        this.active = this.activeItemId === this.item.id;

        // If the aside has a children that is active,
        // always mark it as active
        if ( this._hasCurrentUrlAsChildren(this.item, url) )
        {
            this.active = true;
        }

        // Mark for check
        this._changeDetectorRef.markForCheck();
    }
    
  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Track by function for ngFor loops
   *
   * @param index
   * @param item
   */
  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  sanitizeUrl(u: string): string {
    return (u || '')
      .replace(/^\/+/, '')
      .replace(/[?#&=]/g, '-')
      .replace(/[^a-zA-Z0-9_-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase();
  }

    protected _onDestroy = new Subject<void>();
}
